// Common reusable components
import { useState, useEffect, useRef } from "react";
import { COLORS, ORIENTATION_LABELS, FOUNDER_LABELS, GOVERNANCE_LABELS, MONETIZATION_LABELS,
         ORIENTATION_DEFINITIONS, FOUNDER_DEFINITIONS, GOVERNANCE_DEFINITIONS, MONETIZATION_DEFINITIONS } from "../../lib/constants";

// Badge component with tooltip
export const Badge = ({ type, variant = "orientation", showTooltip = true }) => {
  const labels = variant === "orientation" ? ORIENTATION_LABELS : 
                 variant === "founder" ? FOUNDER_LABELS : 
                 variant === "governance" ? GOVERNANCE_LABELS :
                 MONETIZATION_LABELS;
  
  const definitions = variant === "orientation" ? ORIENTATION_DEFINITIONS : 
                      variant === "founder" ? FOUNDER_DEFINITIONS : 
                      variant === "governance" ? GOVERNANCE_DEFINITIONS :
                      MONETIZATION_DEFINITIONS;
  
  const definition = definitions[type] || "";
  
  return (
    <span 
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold badge-${type} cursor-help relative group`}
      title={showTooltip ? definition : undefined}
    >
      {labels[type] || type}
      {showTooltip && definition && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 text-left font-normal z-50 shadow-xl border border-zinc-700">
          {definition}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900"></span>
        </span>
      )}
    </span>
  );
};

// Animated Number Hook
const useAnimatedNumber = (target, duration = 1200) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!target || target === 0) { setValue(0); return; }
    const start = startRef.current || 0;
    startRef.current = target;
    const diff = target - start;
    let raf;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(start + diff * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
};

// Stats Card with animated counter and sparkline
export const StatCard = ({ title, value, icon: Icon, subtitle, color = "primary", sparkline, description }) => {
  const [showDesc, setShowDesc] = useState(false);
  const animatedValue = useAnimatedNumber(typeof value === "number" ? value : 0);
  const displayValue = typeof value === "number" ? animatedValue.toLocaleString() : value;

  return (
    <div
      className={`bg-card border border-border rounded-lg p-5 card-hover animate-fade-in ${description ? "cursor-pointer" : ""}`}
      data-testid={`stat-card-${title?.replace(/\s/g, "-").toLowerCase()}`}
      onClick={() => description && setShowDesc(prev => !prev)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-muted-foreground text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold mt-1 tabular-nums" style={{ color: COLORS[color] || `hsl(var(--${color}))` }}>
            {displayValue}
          </p>
          {subtitle && <p className="text-muted-foreground text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg bg-muted flex-shrink-0">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
      {/* Sparkline */}
      {sparkline && sparkline.length > 2 && (
        <div className="mt-3 h-8 w-full">
          <MiniSparkline data={sparkline} color={COLORS[color] || "hsl(var(--primary))"} />
        </div>
      )}
      {/* Description on tap */}
      {showDesc && description && (
        <div className="mt-3 pt-3 border-t border-border/50 text-xs leading-relaxed text-muted-foreground" data-testid={`stat-desc-${title?.replace(/\s/g, "-").toLowerCase()}`}>
          {description}
        </div>
      )}
    </div>
  );
};

// Mini Sparkline using inline SVG (no heavy deps)
const MiniSparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.v);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 200;
  const h = 32;
  const pad = 1;

  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  const linePath = `M ${points.join(" L ")}`;
  const areaPath = `${linePath} L ${w - pad},${h} L ${pad},${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-${color.replace(/[^a-z0-9]/gi, "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-${color.replace(/[^a-z0-9]/gi, "")})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Modal Component
export const Modal = ({ children, onClose, title, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className={`relative bg-card border border-border rounded-xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto animate-fade-in`}>
      <div className="sticky top-0 flex items-center justify-between p-4 border-b border-border bg-card z-10">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg" data-testid="modal-close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);
